/**
 * AudioEngine — single AudioContext, master bus, FeatureBus inside tick.
 * Gesture-gated unlock; never throws into the frame loop.
 */

import type { SimView } from "../core/simView";
import type { SimParams } from "../sim/params";
import { audioVoiceRegistry } from "../plugins/index";
import {
  extractAudioFeatures,
  type FeatureBusPrev,
} from "./features";
import type { AudioParams } from "./params";

export type AudioEngineStatus = "idle" | "locked" | "running" | "suspended";

export type AudioEngineOpts = {
  /** Inject for tests */
  contextFactory?: () => AudioContext;
};

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private transportGain: GainNode | null = null;
  private unlocked = false;
  private prev: FeatureBusPrev = { epoch: -1 };
  private speedsScratch: Float64Array | null = null;
  private frameStamp = 0;
  private started = new Set<string>();
  private playing = true;
  private visible = true;
  private lastAudioMs = 0;
  private readonly contextFactory: () => AudioContext;

  constructor(opts?: AudioEngineOpts) {
    this.contextFactory =
      opts?.contextFactory ??
      (() => new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)());
  }

  get status(): AudioEngineStatus {
    if (!this.ctx) return this.unlocked ? "locked" : "idle";
    if (this.ctx.state === "running") return "running";
    if (this.ctx.state === "suspended") return "suspended";
    return "locked";
  }

  /** Last tick wall cost (ms) for adaptive quality EMA. */
  get lastTickMs(): number {
    return this.lastAudioMs;
  }

  /**
   * Construct context + resume. Safe to call repeatedly.
   * Must be invoked from a user-gesture stack when first enabling.
   */
  async unlock(): Promise<void> {
    try {
      if (!this.ctx) {
        this.ctx = this.contextFactory();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0;
        this.transportGain = this.ctx.createGain();
        this.transportGain.gain.value = 1;
        this.master.connect(this.transportGain);
        this.transportGain.connect(this.ctx.destination);
      }
      if (this.ctx.state === "suspended") {
        await this.ctx.resume();
      }
      this.unlocked = true;
    } catch (err) {
      console.warn("[audio] unlock failed", err);
    }
  }

  setPlaying(playing: boolean): void {
    this.playing = playing;
    this.rampTransport();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.rampTransport();
  }

  private rampTransport(): void {
    if (!this.ctx || !this.transportGain) return;
    const t = this.ctx.currentTime;
    const target = this.playing && this.visible ? 1 : 0;
    // Gentle fade on pause / tab hide — no hard mute cut
    this.transportGain.gain.setTargetAtTime(target, t, 0.28);
  }

  /**
   * One audio tick after sim work. No-ops cheaply when disabled and locked.
   */
  tick(
    view: SimView,
    sim: SimParams,
    audio: AudioParams,
    opts?: { dtSec?: number; forceExtract?: boolean },
  ): void {
    const t0 =
      typeof performance !== "undefined" ? performance.now() : 0;

    if (!audio.enabled) {
      this.silenceAll();
      this.lastAudioMs =
        typeof performance !== "undefined" ? performance.now() - t0 : 0;
      return;
    }

    if (!this.ctx || !this.master) {
      // Enabled but not unlocked yet — stay silent (URL boot, etc.)
      this.lastAudioMs =
        typeof performance !== "undefined" ? performance.now() - t0 : 0;
      return;
    }

    if (this.ctx.state === "suspended") {
      // Try resume opportunistically (may no-op without gesture)
      void this.ctx.resume().catch(() => undefined);
    }

    this.frameStamp++;
    if (!this.speedsScratch || this.speedsScratch.length < view.n) {
      this.speedsScratch = new Float64Array(view.n);
    }

    const { frame, next } = extractAudioFeatures(view, sim, audio, this.prev, {
      dtSec: opts?.dtSec ?? 0,
      speedsScratch: this.speedsScratch,
      frameStamp: this.frameStamp,
    });
    this.prev = next;

    const now = this.ctx.currentTime;
    // Soft master — long ramp so voice blends never click or pump
    this.master.gain.setTargetAtTime(
      Math.max(0, Math.min(1, audio.masterGain)),
      now,
      0.22,
    );

    const voices = audioVoiceRegistry.list();
    for (const voice of voices) {
      const want = voice.enabled(audio, sim);
      const id = voice.id;
      if (want && !this.started.has(id)) {
        try {
          voice.start(
            { ctx: this.ctx, dest: this.master, sampleRate: this.ctx.sampleRate },
            audio,
          );
          this.started.add(id);
        } catch (err) {
          console.warn(`[audio] start ${id}`, err);
        }
      }
      if (want && this.started.has(id)) {
        try {
          voice.process(frame, audio, now);
        } catch (err) {
          console.warn(`[audio] process ${id}`, err);
        }
      }
      if (!want && this.started.has(id)) {
        try {
          voice.stop(now);
        } catch {
          /* */
        }
        this.started.delete(id);
      }
    }

    this.lastAudioMs =
      typeof performance !== "undefined" ? performance.now() - t0 : 0;
  }

  private silenceAll(): void {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(0, now, 0.12);
    for (const id of [...this.started]) {
      const voice = audioVoiceRegistry.tryGet(id);
      if (voice) {
        try {
          voice.stop(now);
        } catch {
          /* */
        }
      }
      this.started.delete(id);
    }
  }

  dispose(): void {
    this.silenceAll();
    if (this.ctx) {
      void this.ctx.close().catch(() => undefined);
    }
    this.ctx = null;
    this.master = null;
    this.transportGain = null;
    this.unlocked = false;
    this.prev = { epoch: -1 };
    this.started.clear();
  }
}
